import { useState } from 'react';
import {
    Button,
    Card,
    Menu,
    MenuItem,
    Stack,
    TableBody,
    TableContainer
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTranslation } from 'react-i18next';

import { ContentWrapper } from '@/components/ContentWrapper';
import CardHeader from '@/components/shared/CardHeader';
import { PrimaryButton } from '@/components/shared/Button';
import Chip, { ChipColorVariant } from '@/components/shared/Chip';
import { SMBold, SMRegular } from '@/typography/Typography';
import {
    Table,
    TableHeader,
    TableHeaderCell,
    TableCell,
    TableCellNoPadding,
    TableRow
} from '@/components/shared/styles/Table';
import { useGetHouseAccounts } from '@/services/paymentsQuery';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { useErrorHandling } from '@/utils/errorHandling';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
    HouseAccountBankConnectionStatus,
    InstaPaidHouseAccount
} from '@/services/PaymentsServiceV2';
import CreateHouseAccountDialog from './CreateHouseAccountDialog';
import EditHouseAccountDialog from './EditHouseAccountDialog';

const CONNECTION_STATE_CHIP: Record<
    HouseAccountBankConnectionStatus,
    { color: ChipColorVariant; labelKey: string }
> = {
    Active: { color: 'success', labelKey: 'house_accounts.state_active' },
    Lapsed: { color: 'warning', labelKey: 'house_accounts.state_lapsed' },
    NotConnected: {
        color: 'muted',
        labelKey: 'house_accounts.state_not_connected'
    }
};

const ConnectionStateChip = ({
    status
}: {
    status: HouseAccountBankConnectionStatus;
}) => {
    const { t } = useTranslation();
    const config =
        CONNECTION_STATE_CHIP[status] ?? CONNECTION_STATE_CHIP.NotConnected;
    return <Chip color={config.color} label={t(config.labelKey)} />;
};

const PayoutCapableChip = ({ capable }: { capable: boolean }) => {
    const { t } = useTranslation();
    const color: ChipColorVariant = capable ? 'success' : 'warning';
    const labelKey = capable
        ? 'house_accounts.payout_capable'
        : 'house_accounts.payout_missing_details';
    return <Chip color={color} label={t(labelKey)} />;
};

const StatusChip = ({ account }: { account: InstaPaidHouseAccount }) => {
    const { t } = useTranslation();
    const color: ChipColorVariant = account.isActive ? 'success' : 'muted';
    const labelKey = account.isActive
        ? 'house_accounts.status_active'
        : 'house_accounts.status_inactive';
    return <Chip color={color} label={t(labelKey)} />;
};

export const HouseAccountsPage = () => {
    const { t } = useTranslation();
    const { setAlert } = useNotificationsSnackbar();
    const { backendErrorHandler } = useErrorHandling();
    const { deleteHouseAccount } = usePaymentsMutations();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editAccount, setEditAccount] = useState<InstaPaidHouseAccount | null>(
        null
    );
    const [deleteAccount, setDeleteAccount] =
        useState<InstaPaidHouseAccount | null>(null);

    // Single anchor + row-account pair drives the one shared row-actions menu
    // (rendered once below, not per row) — same pattern as the Employees and
    // Companies tables.
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
    const [menuAccount, setMenuAccount] = useState<InstaPaidHouseAccount | null>(
        null
    );
    const menuOpen = Boolean(menuAnchorEl);

    const handleMenuOpen = (
        event: React.MouseEvent<HTMLButtonElement>,
        account: InstaPaidHouseAccount
    ) => {
        setMenuAnchorEl(event.currentTarget);
        setMenuAccount(account);
    };
    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setMenuAccount(null);
    };

    // Management view lists ALL accounts, including retired ones.
    const { data, isLoading, isError } = useGetHouseAccounts(true, true);
    const accounts: InstaPaidHouseAccount[] = data?.data ?? [];

    const handleDeleteConfirm = () => {
        if (!deleteAccount) {
            return;
        }
        deleteHouseAccount.mutate(deleteAccount.instaPaidHouseAccountId, {
            onSuccess: () => {
                setAlert(t('house_accounts.delete_success'), 'success');
                setDeleteAccount(null);
            },
            onError: (error) => {
                setDeleteAccount(null);
                backendErrorHandler()(error);
            }
        });
    };

    return (
        <ContentWrapper>
            <>
                <Card>
                    <CardHeader
                        label={t('house_accounts.page_title')}
                        actions={
                            <PrimaryButton onClick={() => setDialogOpen(true)}>
                                <SMBold textTransform="none">
                                    {t('house_accounts.add_account')}
                                </SMBold>
                            </PrimaryButton>
                        }
                    />
                    <TableContainer>
                        <Table aria-label="house accounts">
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCell>
                                        {t('house_accounts.name')}
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        {t('house_accounts.currency')}
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        {t('house_accounts.payout_capability')}
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        {t('house_accounts.connection_state')}
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        {t('house_accounts.status')}
                                    </TableHeaderCell>
                                    <TableHeaderCell align="right">
                                        {t('house_accounts.actions')}
                                    </TableHeaderCell>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {accounts.map((account) => (
                                    <TableRow
                                        key={account.instaPaidHouseAccountId}
                                    >
                                        <TableCell>{account.name}</TableCell>
                                        <TableCell>
                                            {account.currency}
                                        </TableCell>
                                        <TableCell>
                                            <PayoutCapableChip
                                                capable={
                                                    account.isPayoutCapable
                                                }
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <ConnectionStateChip
                                                status={
                                                    account.bankConnectionStatus
                                                }
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <StatusChip account={account} />
                                        </TableCell>
                                        <TableCellNoPadding align="right">
                                            <Button
                                                color="secondary"
                                                aria-label={t(
                                                    'house_accounts.actions'
                                                )}
                                                aria-controls={
                                                    menuOpen
                                                        ? 'house-account-actions-menu'
                                                        : undefined
                                                }
                                                aria-haspopup="true"
                                                aria-expanded={
                                                    menuOpen ? 'true' : undefined
                                                }
                                                onClick={(event) =>
                                                    handleMenuOpen(
                                                        event,
                                                        account
                                                    )
                                                }
                                            >
                                                <MoreVertIcon />
                                            </Button>
                                        </TableCellNoPadding>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                        <Menu
                            id="house-account-actions-menu"
                            anchorEl={menuAnchorEl}
                            open={menuOpen}
                            onClose={handleMenuClose}
                            MenuListProps={{
                                'aria-labelledby': 'house-account-actions-menu'
                            }}
                            sx={{ boxShadow: 'none' }}
                        >
                            <MenuItem
                                onClick={() => {
                                    setEditAccount(menuAccount);
                                    handleMenuClose();
                                }}
                            >
                                {t('house_accounts.edit_account')}
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    setDeleteAccount(menuAccount);
                                    handleMenuClose();
                                }}
                            >
                                {t('house_accounts.delete_account')}
                            </MenuItem>
                        </Menu>
                    </TableContainer>
                    {(isLoading || isError || accounts.length === 0) && (
                        <Stack alignItems="center" p={3}>
                            <SMRegular>
                                {isLoading
                                    ? t('house_accounts.loading')
                                    : isError
                                    ? t('house_accounts.load_error')
                                    : t('house_accounts.empty')}
                            </SMRegular>
                        </Stack>
                    )}
                </Card>

                <CreateHouseAccountDialog
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                />

                <EditHouseAccountDialog
                    account={editAccount}
                    onClose={() => setEditAccount(null)}
                />

                <ConfirmDialog
                    dialogOpen={!!deleteAccount}
                    title={t('house_accounts.delete_confirm_title')}
                    content={t('house_accounts.delete_confirm_content', {
                        name: deleteAccount?.name
                    })}
                    handleCancel={() => setDeleteAccount(null)}
                    handleContinue={handleDeleteConfirm}
                />
            </>
        </ContentWrapper>
    );
};

export default HouseAccountsPage;
